import { observer } from 'mobx-react';
import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { SCREENS } from 'src/constants/screens';
import { crmScreenRoles, dashboardScreenRoles, USER_PERMISSIONS, USER_ROLES, orderHistoryScreenRoles } from 'src/constants/user';
import { appStore } from 'src/mobx/appStore';
import { localeStore } from 'src/mobx/localesStore';
import { hasRole } from 'src/utils/helpers';
import CustomRoute from 'src/view/components/CustomRoute/CustomRoute';
import ModalsContainer from 'src/view/containers/ModalsContainer/ModalsContainer';
import BranchesManagementScreen from 'src/view/screens/BranchesManagementScreen/BranchesManagementScreen';
import CallCenterScreen from 'src/view/screens/CallCenterScreen';
import CrmScreen from 'src/view/screens/CrmScreen/CrmScreen';
import DeliveryManagementScreen from 'src/view/screens/DeliveryManagementScreen/DeliveryManagementScreen';
import GuidelineScreen from 'src/view/screens/GuidelineScreen/GuidelineScreen';
import ItemsInventoryScreen from 'src/view/screens/ItemsInventoryScreen/ItemsInventoryScreen';
import MarketingScreen from 'src/view/screens/MarketingScreen/MarketingScreen';
import MenuManagementScreen from 'src/view/screens/MenuManagementScreen/MenuManagementScreen';
import OopsScreen from 'src/view/screens/OopsScreen/OopsScreen';
import OrdersHistory from 'src/view/screens/OrdersHistory';
import ReportsNavigation from 'src/view/screens/ReportsScreen/containers/ReportsNavigation/ReportsNavigation';
import SettingsRoutes from '~/view/screens/SettingsScreen/routes/SettingsRoutes';
import { needRedirect } from '~/helpers';
import { HistoryCheckoutScreen, LiveOrdersCheckoutScreen, OrdersRatingCheckoutScreen } from '~/view/screens/CheckoutScreen';
import LiveOrdersScreen from '~/view/screens/LiveOrdersScreen';
import { HistoryOrderScreen, RatingOrderScreen } from '~/view/screens/OrderScreen';
import DashboardScreen from '~/view/screens/DashboardScreen/DashboardScreen';
import MarketplaceScreen from '~/view/screens/MarketplaceScreen/MarketplaceScreen';
import MarketplaceFeatureEdit from '~/view/screens/MarketplaceScreen/containers/MarketplaceFeatureEdit/MarketplaceFeatureEdit';
import WalletTransactionLogScreen from '~/view/screens/WalletTransactionLogScreen/WalletTransactionLogScreen';
import FoodicsLogScreen from '~/view/screens/FoodicsLogScreen/FoodicsLogScreen';
import BranchLogScreen from '~/view/screens/BranchLogScreen/BranchesLogScreen';
import ItemManagementScreen from '~/view/screens/MenuManagementScreen/screens/ItemManagementScreen/ItemManagementScreen';
import RefundLogScreen from '~/view/screens/RefundLogScreen/RefundLogScreen';
import CustomerProfileEditScreen from '~/view/screens/CrmScreen/screens/CustomerProfileEditScreen/CustomerProfileEditScreen';
import OrderRatingScreen from '~/view/screens/OrdersRatingScreen/screens/OrderRatingScreen/OrderRatingScreen';
import PromocodeLogScreen from '~/view/screens/PromocodeLogScreen';
import PromocodeEdit from '~/view/screens/MarketingScreen/screens/PromocodesScreen/screens/PromocodeEdit/PromocodeEdit';
import WorkingHoursLogScreen from '~/view/screens/WorkingHoursLogScreen/WorkingHoursLogScreen';
import CrmRestrictionsEdit from '~/view/screens/CrmScreen/screens/CrmRestrictionsScreen/screens/CrmRestrictionsEdit/CrmRestrictionsEdit';

@observer
class PrivateRouter extends React.Component<{}, {}> {
  public render() {
    if (!appStore.settings || !localeStore.localizationLoaded) return null;
    const redirectPath = needRedirect();

    return (
      <React.Fragment>
        <Switch>
          {/* Redirect to order details by user role */}
          {redirectPath ? (
            <Route path="*">
              <Redirect to={redirectPath} />
            </Route>
          ) : null}

          <CustomRoute permissions={USER_PERMISSIONS.ORDERS_EDIT} exact path={SCREENS.LIVE_ORDER_EDIT(':id')} component={LiveOrdersCheckoutScreen} />
          <CustomRoute
            exact
            permissions={USER_PERMISSIONS.ORDERS_VIEW}
            path={[
              SCREENS.LIVE_ORDERS(),
              SCREENS.LIVE_ORDER(':id'),
              SCREENS.LIVE_ORDER_LOGS(':id'),
              SCREENS.LIVE_ORDER_TECHNICAL_LOGS(':id'),
              SCREENS.LIVE_ORDER_TRANSACTION_LOGS(':id'),
              SCREENS.LIVE_ORDER_CONTENT_LOGS(':id'),
            ]}
            component={LiveOrdersScreen}
          />

          <CustomRoute
            exact
            permissions={USER_PERMISSIONS.ORDERS_VIEW}
            roles={[USER_ROLES.CALL_CENTER_AGENT, USER_ROLES.RESTAURANT_MANAGER, USER_ROLES.OPERATOR]}
            path={[
              SCREENS.CALLCENTER(),
              SCREENS.CALLCENTER_ORDER(':id'),
              SCREENS.CALLCENTER_ORDER_EDIT(':id'),
              SCREENS.CALLCENTER_ORDER_LOGS(':id'),
              SCREENS.CALLCENTER_ORDER_TECHNICAL_LOGS(':id'),
              SCREENS.CALLCENTER_ORDER_TRANSACTION_LOGS(':id'),
              SCREENS.CALLCENTER_ORDER_CONTENT_LOGS(':id'),
            ]}
            component={CallCenterScreen}
          />

          <CustomRoute exact permissions={USER_PERMISSIONS.ORDERS_VIEW} path={[SCREENS.HISTORY()]} component={OrdersHistory} />
          <CustomRoute permissions={USER_PERMISSIONS.ORDERS_EDIT} exact path={SCREENS.HISTORY_ORDER_EDIT(':id')} component={HistoryCheckoutScreen} />
          <CustomRoute
            exact
            path={[
              SCREENS.HISTORY_ORDER_VIEW(':id'),
              SCREENS.HISTORY_ORDER_LOGS(':id'),
              SCREENS.HISTORY_ORDER_TECHNICAL_LOGS(':id'),
              SCREENS.HISTORY_ORDER_CONTENT_LOGS(':id'),
              SCREENS.HISTORY_ORDER_TRANSACTION_LOGS(':id'),
            ]}
            roles={orderHistoryScreenRoles}
            permissions={USER_PERMISSIONS.ORDERS_VIEW}
            component={HistoryOrderScreen}
          />

          {/* dashboard screen */}
          <CustomRoute roles={dashboardScreenRoles} path={[SCREENS.DASHBOARD()]} component={DashboardScreen} />

          {/* items and inventory screen */}
          <Route exact path={SCREENS.ITEMS_AND_INVENTORY()}>
            <Redirect to={SCREENS.PRODUCTS()} />
          </Route>
          <CustomRoute
            permissions={USER_PERMISSIONS.BRANCH_ITEMS_VIEW}
            path={[SCREENS.ITEMS_AND_INVENTORY(), SCREENS.PRODUCTS(), SCREENS.DEAL_COMBOS()]}
            component={ItemsInventoryScreen}
          />

          {/* reports screen */}
          <CustomRoute
            exact
            permissions={USER_PERMISSIONS.REPORTS}
            roles={[USER_ROLES.RESTAURANT_MANAGER, USER_ROLES.FRANCHISEE, USER_ROLES.ACCOUNTING]}
            path={[
              SCREENS.REPORTS(),
              SCREENS.HOURS_SALES(),
              SCREENS.DAY_WEEK_SALES(),
              SCREENS.MONTH_YEAR_SALES(),
              SCREENS.BRANCH_SALES(),
              SCREENS.AREA_SALES(),
              SCREENS.CALL_CENTER_AGENT_SALES(),
              SCREENS.ITEM_SALES(),
              SCREENS.PAYMENT_METHOD_SALES(),
              SCREENS.ORDERING_CHANNEL_SALES(),
              SCREENS.COMBO_SALES(),
            ]}
            component={ReportsNavigation}
          />

          {/* marketing screen */}
          <Route exact path={SCREENS.MARKETING()}>
            <Redirect to={SCREENS.PROMOCODES()} />
          </Route>

          <CustomRoute path={[SCREENS.PROMOCODE_EDIT(':id'), SCREENS.PROMOCODE_CREATE()]} component={PromocodeEdit} />

          <CustomRoute
            exact
            permissions={USER_PERMISSIONS.PROMOTION}
            path={[
              SCREENS.MARKETING(),
              SCREENS.PROMOCODES(),
              SCREENS.PROMOBANNERS(),
              SCREENS.PROMOBANNER_EDIT(':id'),
              SCREENS.PROMOBANNER_CREATE(),
              SCREENS.MESSAGE_CAMPAIGN_CREATE(),
              SCREENS.MESSAGE_CAMPAIGNS(),
              SCREENS.MESSAGE_CAMPAIGN_EDIT(':id'),
              SCREENS.DEALS(),
              SCREENS.DEALS_EDIT(':id'),
              SCREENS.DEALS_CREATE(),
              SCREENS.CASHBACK(),
              SCREENS.CASHBACK_EDIT(':id'),
              SCREENS.CASHBACK_CREATE(),
            ]}
            component={MarketingScreen}
          />

          {/* crm screen */}
          <CustomRoute
            roles={crmScreenRoles}
            path={[SCREENS.CRM_PROFILE_EDIT(':id'), SCREENS.CRM_PROFILE_CREATE()]}
            component={CustomerProfileEditScreen}
          />
          {/* <Route exact path={SCREENS.CRM()}>
            <Redirect to={SCREENS.ORDERS_FEEDBACK()} />
          </Route> */}
          <CustomRoute
            exact
            roles={[]}
            path={[
              SCREENS.ORDER_RATING_ORDER(':id'),
              SCREENS.ORDER_RATING_ORDER_LOGS(':id'),
              SCREENS.ORDER_RATING_ORDER_TECHNICAL_LOGS(':id'),
              SCREENS.ORDER_RATING_ORDER_TRANSACTION_LOGS(':id'),
              SCREENS.ORDER_RATING_ORDER_CONTENT_LOGS(':id'),
            ]}
            component={RatingOrderScreen}
          />
          <CustomRoute exact roles={[]} path={[SCREENS.ORDER_RATING_ORDER_EDIT(':id')]} component={OrdersRatingCheckoutScreen} />
          <CustomRoute exact roles={crmScreenRoles} path={[SCREENS.ORDER_RATING(':id')]} component={OrderRatingScreen} />
          <CustomRoute
            exact
            roles={crmScreenRoles}
            path={[SCREENS.CRM_PROFILES(), SCREENS.ORDERS_RATING(), SCREENS.CRM_RESTRICTIONS()]}
            component={CrmScreen}
          />
          <CustomRoute roles={crmScreenRoles} path={[SCREENS.CRM_RESTRICTIONS_CREATE()]} component={CrmRestrictionsEdit} />

          {/* marketplace */}
          <CustomRoute exact roles={[USER_ROLES.RESTAURANT_MANAGER]} path={[SCREENS.MARKETPLACE()]} component={MarketplaceScreen} />
          <CustomRoute
            exact
            roles={[USER_ROLES.RESTAURANT_MANAGER]}
            path={[SCREENS.MARKETPLACE_FEATURE(':featureKey')]}
            component={MarketplaceFeatureEdit}
          />

          {/* delivery management screen */}
          <Route exact path={SCREENS.DELIVERY_MANAGEMENT()}>
            <Redirect to={SCREENS.DELIVERY_ZONES()} />
          </Route>
          <CustomRoute
            exact
            permissions={[]}
            roles={[USER_ROLES.RESTAURANT_MANAGER]}
            path={[
              SCREENS.DELIVERY_MANAGEMENT(),
              SCREENS.DELIVERY_ZONES(),
              SCREENS.DISPATCHING_RULES(),
              // SCREENS.DELIVERY_TRACKING(),
              // SCREENS.DELIVERY_TRACKING_REQUEST(':id'),
              // SCREENS.DSP_COVERAGE(),
            ]}
            component={DeliveryManagementScreen}
          />

          {/* menu management screen */}
          <Route exact path={SCREENS.MENU()}>
            <Redirect to={SCREENS.MENU_GROUPS()} />
          </Route>
          <CustomRoute
            exact
            permissions={[]}
            roles={[USER_ROLES.RESTAURANT_MANAGER]}
            path={[
              SCREENS.CATEGORIES(),
              SCREENS.CATEGORIES_CREATE(),
              SCREENS.CATEGORIES_EDIT(':id'),
              SCREENS.ITEMS(),
              SCREENS.MODIFIERS(),
              SCREENS.MODIFIERS_CREATE(),
              SCREENS.MODIFIERS_EDIT(':id'),
              SCREENS.EXCLUSIONS(),
              SCREENS.EXCLUSIONS_CREATE(),
              SCREENS.EXCLUSIONS_EDIT(':id'),
            ]}
            component={MenuManagementScreen}
          />
          <CustomRoute
            exact
            permissions={[]}
            roles={[USER_ROLES.RESTAURANT_MANAGER]}
            path={[
              SCREENS.ITEMS_CREATE(),
              SCREENS.ITEMS_EDIT(':id'),
              SCREENS.ITEMS_BRANCHES_CONFIGURATION(':id'),
              SCREENS.ITEMS_BRANCHES_MODIFIERS(':id'),
            ]}
            component={ItemManagementScreen}
          />
          {/* menu management screen include Marketing role */}
          <CustomRoute
            exact
            roles={[USER_ROLES.RESTAURANT_MANAGER, USER_ROLES.MARKETING]}
            path={[
              SCREENS.MENU(),
              SCREENS.MENU_GROUPS(),
              SCREENS.MENU_GROUPS_CREATE(),
              SCREENS.MENU_GROUPS_EDIT(':id'),
              SCREENS.MENU_GROUP_IMPROVEMENT(),
              SCREENS.MEALS(),
              SCREENS.MEALS_CREATE(),
              SCREENS.MEALS_EDIT(':id'),
            ]}
            component={MenuManagementScreen}
          />

          {/* branch management screen */}
          <CustomRoute
            exact
            roles={[USER_ROLES.RESTAURANT_MANAGER, USER_ROLES.FRANCHISEE]}
            path={[SCREENS.BRANCHES(), SCREENS.BRANCHES_WORKING_HOURS(), SCREENS.BRANCHES_CREATE(), SCREENS.BRANCHES_EDIT(':id')]}
            component={BranchesManagementScreen}
          />

          {/* Working hours logs screen */}
          <CustomRoute
            exact
            roles={[USER_ROLES.RESTAURANT_MANAGER]}
            path={[SCREENS.BRANCHES_WORKING_HOURS_LOG(':id')]}
            component={WorkingHoursLogScreen}
          />

          {/* Branch logs screen */}
          <CustomRoute
            exact
            roles={[USER_ROLES.RESTAURANT_MANAGER]}
            path={[SCREENS.BRANCHES_LOG(), SCREENS.BRANCH_LOG(':id')]}
            component={BranchLogScreen}
          />

          {/* Wallet Transaction Log screen */}
          <CustomRoute
            exact
            roles={[USER_ROLES.RESTAURANT_MANAGER]}
            path={[SCREENS.WALLET_TRANSACTION_LOG(), SCREENS.USER_WALLET_TRANSACTION_LOG(':id')]}
            component={WalletTransactionLogScreen}
          />

          {/* Foodics Log */}
          <CustomRoute exact roles={[USER_ROLES.RESTAURANT_MANAGER]} path={[SCREENS.FOODICS_LOG()]} component={FoodicsLogScreen} />

          {/* Refund Log */}
          <CustomRoute
            exact
            roles={[USER_ROLES.RESTAURANT_MANAGER, USER_ROLES.ACCOUNTING, USER_ROLES.FRANCHISEE]}
            path={[SCREENS.REFUND_LOG()]}
            component={RefundLogScreen}
          />

          <CustomRoute
            exact
            roles={[USER_ROLES.RESTAURANT_MANAGER, USER_ROLES.MARKETING, USER_ROLES.ACCOUNTING, USER_ROLES.FRANCHISEE]}
            path={[SCREENS.PROMOCODE_LOG()]}
            component={PromocodeLogScreen}
          />

          {/* settings screen */}
          <Route exact path={SCREENS.SETTINGS_WEBSITE()}>
            <Redirect to={SCREENS.SETTINGS_WEBSITE_ABOUT()} />
          </Route>
          <Route exact path={SCREENS.SETTINGS_ORDERS()}>
            <Redirect to={SCREENS.SETTINGS_PREPARATION_PROFILES()} />
          </Route>
          <CustomRoute
            exact
            roles={[USER_ROLES.RESTAURANT_MANAGER]}
            path={[
              SCREENS.SETTINGS(),
              // general
              SCREENS.SETTINGS_USERS(),
              SCREENS.SETTINGS_USER_ROLES(),
              SCREENS.SETTINGS_USERS_EDIT(':id'),
              SCREENS.SETTINGS_USERS_CREATE(),
              SCREENS.SETTINGS_COUNTRIES(),
              SCREENS.SETTINGS_COUNTRY_EDIT(':id'),
              SCREENS.SETTINGS_COUNTRY_CREATE(),
              SCREENS.SETTINGS_CURRENCY(),
              SCREENS.SETTINGS_CURRENCY_EDIT(':id'),
              SCREENS.SETTINGS_CURRENCY_CREATE(),
              // SCREENS.SETTINGS_TRANSLATION(),
              // SCREENS.SETTINGS_TRANSLATION_EDIT(':id'),
              // SCREENS.SETTINGS_TRANSLATION_CREATE(),
              // ordering channels
              SCREENS.SETTINGS_CONSUMER_CHANNELS_CONFIG(),
              SCREENS.SETTINGS_CONSUMER_CHANNELS_CONFIG_MOBILE(),
              SCREENS.SETTINGS_CONSUMER_CHANNELS_CONFIG_WEB(),
              SCREENS.SETTINGS_CONSUMER_CHANNELS_CONFIG_KIOSK(),
              SCREENS.SETTINGS_CONSUMER_CHANNELS_CONFIG_DOOK(),
              SCREENS.SETTINGS_WEBSITE_ABOUT(),
              SCREENS.SETTINGS_WEBSITE_CONTACT_US(),
              SCREENS.SETTINGS_WEBSITE_COOKIES(),
              SCREENS.SETTINGS_WEBSITE_FRANCHISE(),
              SCREENS.SETTINGS_WEBSITE_MANAGE_FAQ(),
              SCREENS.SETTINGS_WEBSITE_MANAGE_FAQ_EDIT(':id'),
              SCREENS.SETTINGS_WEBSITE_MANAGE_FAQ_CREATE(),
              SCREENS.SETTINGS_WEBSITE_PRIVACY_POLICY(),
              SCREENS.SETTINGS_WEBSITE_TERMS_CONDITIONS(),
              // orders
              SCREENS.SETTINGS_ORDERS(),
              SCREENS.SETTINGS_ORDERS_CONDITIONS(),
              SCREENS.SETTINGS_ORDERS_NOTES(),
              SCREENS.SETTINGS_ORDERS_FRANCHISE(),
              SCREENS.SETTINGS_PREPARATION_PROFILES(),
              // devices
              SCREENS.SETTINGS_DEVICES_KIOSKS(),
              SCREENS.SETTINGS_DEVICES_KIOSKS_CREATE(),
              SCREENS.SETTINGS_DEVICES_KIOSKS_EDIT(':id'),
              // connections
              SCREENS.SETTINGS_WEBHOOK(),
              SCREENS.SETTINGS_WEBHOOK_EDIT(':id'),
              SCREENS.SETTINGS_WEBHOOK_CREATE(),
              // communications
              SCREENS.SETTINGS_NOTIFICATIONS(),
              SCREENS.SETTINGS_NOTIFICATIONS_ORDER_STATUS(),
              SCREENS.SETTINGS_NOTIFICATIONS_ORDER_STATUS_CREATE(),
              SCREENS.SETTINGS_NOTIFICATIONS_ORDER_SYSTEM(),
              SCREENS.SETTINGS_NOTIFICATIONS_WALLET(),
              SCREENS.SETTINGS_NOTIFICATIONS_WALLET_CREATE(),
              SCREENS.SETTINGS_NOTIFICATIONS_CURBSIDE(),
              // integrations
              SCREENS.SETTINGS_INTEGRATIONS(),
              SCREENS.SETTINGS_INTEGRATION(':id'),
            ]}
            component={SettingsRoutes}
          />

          {/* error screen */}
          <Route exact path={SCREENS.NOT_FOUND()} component={OopsScreen} />

          {/* developers screens. Not for customers */}
          {process.env.NODE_ENV === 'development' ? <Route exact path={SCREENS.GUIDELINE()} component={GuidelineScreen} /> : null}

          {/* redirect to default screen */}
          {hasRole([USER_ROLES.RESTAURANT_MANAGER]) ? (
            <Route exact path={SCREENS.REST()}>
              <Redirect to={SCREENS.DASHBOARD()} />
            </Route>
          ) : null}

          {hasRole([USER_ROLES.ACCOUNTING]) ? (
            <Route exact path={SCREENS.REST()}>
              <Redirect to={SCREENS.REPORTS()} />
            </Route>
          ) : null}

          {hasRole([USER_ROLES.CUSOMER_CARE]) ? (
            <Route exact path={SCREENS.REST()}>
              <Redirect to={SCREENS.CRM_PROFILES()} />
            </Route>
          ) : null}

          {hasRole([USER_ROLES.MARKETING]) ? (
            <Route exact path={SCREENS.REST()}>
              <Redirect to={SCREENS.MARKETING()} />
            </Route>
          ) : null}

          {hasRole([USER_ROLES.CALL_CENTER_AGENT]) ? (
            <Route exact path={SCREENS.REST()}>
              <Redirect to={SCREENS.CALLCENTER()} />
            </Route>
          ) : null}

          <Route path={SCREENS.REST()}>
            <Redirect to={SCREENS.LIVE_ORDERS()} />
          </Route>
        </Switch>
        <ModalsContainer />
      </React.Fragment>
    );
  }
}

export default PrivateRouter;
